import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Select, TextField, DateInput, DatePicker, GroupSearchSelect } from '../../../components/ReactHookForm';
import { ChevronLeft } from '@td-client-frontend/icons';
import { gender, language, relationship } from '../../../utils/members/options';
import {
  genderFormatter,
  dateFormatter,
  languageFormatter,
  relationshipFormatter
} from '../../../utils/members/fieldsFormatters';

interface Props {
  isCreate: boolean;
  onBack?: () => void | null;
}

export default function AccountInformation({ isCreate, onBack }: Props) {
  const { watch } = useFormContext();
  const isPrimaryFlg = watch('primaryFlg') === 'Y';

  const readOnly = watch('readOnly');
  const isClientIdRequired = watch('group')?.consultReimbursementMethodCode === 'CONSULTREIMBURSEMENT_CLAIM';

  return (
    <>
      <div className="flex justify-between">
        {isPrimaryFlg ? (
          <div className="grid gap-4 grid-cols-3">
            {isCreate ? (
              <GroupSearchSelect
                name="group"
                label="Group *"
                placeholder="Select..."
                includeAll={false}
                initialSearchTerm={watch('groupSearchTerm')}
                groupRegistrationCodes={['REGTYPE_STAGEDCLIENT', 'REGTYPE_OPEN']}
              />
            ) : (
              <TextField name="group.name" label="Group" readOnly />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4 w-1/3 items-start">
            {onBack && (
              <button className="font-medium text-primary-600 flex items-center" onClick={onBack}>
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back to view all dependents
              </button>
            )}
            <h2 className="mb-4">{isCreate ? 'Add dependent' : 'Manage dependent'}</h2>
            <Select
              name="relationshipSubtypeCd"
              label="Relationship"
              options={relationship}
              placeholder="Select..."
              required
              readOnlyFieldFormatter={relationshipFormatter}
            />
          </div>
        )}
        {!readOnly && <p>*Required</p>}
      </div>
      <div className="flex flex-col">
        <h2 className="mb-4">Demographics</h2>
        <div className="grid gap-4 grid-cols-3">
          <TextField name="firstNm" label="First Name" placeholder="Enter first name" required />
          <TextField name="middleNm" label="Middle Name" placeholder="Enter middle name" />
          <TextField name="lastNm" label="Last Name" placeholder="Enter last name" required />
        </div>
        <div className="grid gap-4 grid-cols-5">
          <DateInput name="dob" label="Date of Birth" placeholder="MM/DD/YYYY" required />
          <Select
            name="genderCd"
            label="Gender"
            options={gender}
            placeholder="Select..."
            required
            readOnlyFieldFormatter={genderFormatter}
          />
          <Select
            name="languageCd"
            label="Language"
            options={language}
            placeholder="Select..."
            required
            readOnlyFieldFormatter={languageFormatter}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <h2 className="mb-4">Plan Details</h2>
        <div className="flex gap-4">
          <TextField name="clientId" label="Client Member ID" wrapperClasses="w-52" required={isClientIdRequired} />
          {!isPrimaryFlg && (
            <TextField name="contactEmail" label="Email" wrapperClasses="w-52" placeholder="Enter email" />
          )}
          <DatePicker
            name="effectiveStartDt"
            label="Start Date"
            wrapperClasses="w-36"
            placeholder="MM/DD/YYYY"
            required
            readOnlyFieldFormatter={dateFormatter}
          />
          {isPrimaryFlg && !isCreate && (
            <DatePicker
              name="effectiveEndDt"
              label="Term Date"
              wrapperClasses="w-36"
              placeholder="MM/DD/YYYY"
              disabled
              readOnlyFieldFormatter={dateFormatter}
            />
          )}
        </div>
      </div>
    </>
  );
}
